import React, { useState } from 'react';
import { MatterList, MatterForm, MatterStats } from '../components/matters';
import type { Matter, MatterFormData, UpdateMatterRequest } from '../types';

// Sample matter data for demonstration
const sampleMatters: Matter[] = [
  {
    id: '1',
    clientId: '1',
    title: 'Employment Contract Review',
    type: 'Employment Law',
    status: 'active',
    description: 'Review and negotiation of executive employment contracts for senior management positions.',
    dateCreated: new Date('2024-03-01'),
    lastActivity: new Date('2024-03-15'),
    billedAmount: 12500.00,
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-03-15')
  },
  {
    id: '2',
    clientId: '1',
    title: 'Intellectual Property Registration',
    type: 'Intellectual Property',
    status: 'active',
    description: 'Trademark and patent applications for new software products.',
    dateCreated: new Date('2024-02-15'),
    lastActivity: new Date('2024-03-10'),
    billedAmount: 8750.00,
    createdAt: new Date('2024-02-15'),
    updatedAt: new Date('2024-03-10')
  },
  {
    id: '3',
    clientId: '1',
    title: 'Corporate Restructuring',
    type: 'Corporate Law',
    status: 'completed',
    description: 'Complete corporate restructuring and subsidiary formation.',
    dateCreated: new Date('2024-01-20'),
    lastActivity: new Date('2024-02-28'),
    billedAmount: 24500.00,
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-02-28')
  },
  {
    id: '4',
    clientId: '1',
    title: 'Contract Negotiation',
    type: 'Contract Law',
    status: 'on-hold',
    description: 'Vendor contract negotiations for technology services.',
    dateCreated: new Date('2024-01-10'),
    lastActivity: new Date('2024-02-05'),
    billedAmount: 3200.00,
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-02-05')
  }
];

const MatterComponentsDemo: React.FC = () => {
  const [matters, setMatters] = useState<Matter[]>(sampleMatters);
  const [showForm, setShowForm] = useState(false);
  const [editingMatter, setEditingMatter] = useState<Matter | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  // Handle form submission (create or edit)
  const handleFormSubmit = async (formData: MatterFormData | UpdateMatterRequest) => {
    if (editingMatter) {
      // Editing existing matter
      setIsLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setMatters(prev => prev.map(matter => 
        matter.id === editingMatter.id 
          ? { ...matter, ...formData, updatedAt: new Date() }
          : matter
      ));
      
      setEditingMatter(undefined);
      setShowForm(false);
      setIsLoading(false);
    } else {
      // Creating new matter
      const createData = formData as MatterFormData;
      setIsLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newMatter: Matter = {
        id: String(matters.length + 1),
        clientId: createData.clientId, // Use clientId from form data
        title: createData.title,
        type: createData.type,
        status: createData.status || 'active',
        description: createData.description,
        dateCreated: new Date(),
        lastActivity: new Date(),
        billedAmount: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setMatters(prev => [...prev, newMatter]);
      setShowForm(false);
      setIsLoading(false);
    }
  };

  // Handle matter click
  const handleMatterClick = (matter: Matter) => {
    console.log('Matter clicked:', matter);
    alert(`Clicked on matter: ${matter.title}`);
  };

  // Handle edit button click
  const handleEditClick = (matter: Matter) => {
    setEditingMatter(matter);
    setShowForm(true);
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setEditingMatter(undefined);
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            Matter Management Components Demo
          </h1>
          <p className="text-gray-400">
            Demonstrating MatterStats, MatterList, and MatterForm components
          </p>
        </div>

        {/* Matter Statistics */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4">Matter Statistics</h2>
          <MatterStats matters={matters} isLoading={false} />
        </div>

        {/* Add Matter Button */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Matters List</h2>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Add New Matter
          </button>
        </div>

        {/* Matter Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="w-full max-w-2xl">
              <MatterForm
                matter={editingMatter}
                isLoading={isLoading}
                onSubmit={handleFormSubmit}
                onCancel={handleFormCancel}
              />
            </div>
          </div>
        )}

        {/* Matter List */}
        <MatterList
          matters={matters}
          isLoading={false}
          onMatterClick={handleMatterClick}
          onEditMatter={handleEditClick}
        />

        {/* Usage Instructions */}
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-3">Demo Instructions</h3>
          <ul className="text-gray-300 space-y-2">
            <li>• <strong>View Statistics:</strong> The top section shows calculated matter statistics</li>
            <li>• <strong>Click Matters:</strong> Click on any matter card to see the click handler</li>
            <li>• <strong>Edit Matters:</strong> Hover over matters and click the edit icon</li>
            <li>• <strong>Add Matters:</strong> Use the "Add New Matter" button to create new matters</li>
            <li>• <strong>Form Validation:</strong> Try submitting empty or invalid forms to see validation</li>
            <li>• <strong>Keyboard Shortcuts:</strong> Use Ctrl+Enter in forms to submit</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MatterComponentsDemo;
